// @ts-nocheck
import {getColumnsWithSortDirection} from '/ui/components/table/common';
import * as Types from '/ui/components/table/types';

export const ColActionTypes = Types.ColActionTypes;

/**
 * @param {!Array<!Types.Column>} columns
 * @param {Types.ColAction} colAction
 * @returns {!Array<!Types.Column>}
 */
function colReducer(columns, colAction) {
  switch (colAction.type) {
    case ColActionTypes.SET_COLUMNS:
      return colAction.columns;
    case ColActionTypes.SET_SORT_DIR:
      return getColumnsWithSortDirection(columns, colAction.colId, colAction.newSortDirection);
    case ColActionTypes.COL_PICKER_SELECT:
      return columns.map(col => ({
        ...col,
        hidden: !colAction.selectedValues.includes(col.id),
      }));
    case ColActionTypes.COL_PICKER_RESET:
      return columns.map(col => ({
        ...col,
        hidden: (colAction.defaultColIds && !colAction.defaultColIds.includes(col.id)) || false,
      }));
    default:
      return columns;
  }
}

/**
 * A reducer hook which handles Table Column state manipulation.
 *
 * Typical column manipulations are handled by the provided Column actions.
 * Custom manipulation not covered by the provided actions can be done via
 * ColActionTypes.SET_COLUMNS (or by editing the reducer above!).
 *
 * See React's useReducer hook docs:
 * https://reactjs.org/docs/hooks-reference.html#usereducer
 *
 * @param {Array<!Types.Column>} defaultColumns
 * @param {function(Array<!Types.Column>):!Array<!Types.Column>=} initDefaultColumns
 * @returns {{
 *   columns: !Array<!Types.Column>,
 *   columnDispatch: function(!Types.ColAction):void
 * }}
 */
export function useColumnReducer(defaultColumns, initDefaultColumns) {
  const [columns, columnDispatch] = React.useReducer(colReducer, defaultColumns, initDefaultColumns);
  return {columns, columnDispatch};
}

/**
 * Hook to create row selection handlers for data that doesn't require
 * pagination.
 *
 * @param {!Array<Types.Row>} data
 * @param {function(Set<string>): void} onSelectionChange
 * @param {boolean=} isSingleSelect
 * @param {(string | !Array<string>)=} initiallySelectedIds
 * @returns {Types.RowSelectionOptions}
 */
export function useSimpleSelection(data, onSelectionChange, isSingleSelect = false, initiallySelectedIds = []) {
  const [selectedIds, setSelectedIds] = React.useState(new Set([].concat(initiallySelectedIds)));
  React.useEffect(() => {
    onSelectionChange(selectedIds);
  }, [onSelectionChange, selectedIds]);

  const allIds = data.map(({id}) => id);

  const isSelected = id => {
    return selectedIds.has(id);
  };

  const selectOne = (id, state) => {
    const newSelected = new Set(isSingleSelect ? null : selectedIds);
    if (state) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  const rowSelectionOptions = {
    isSelected,
    onSelectRow: selectOne,
    selectedIds: selectedIds,
    isSingleSelect: !!isSingleSelect,
  };

  if (isSingleSelect) {
    return rowSelectionOptions;
  }

  const selectNone = () => {
    setSelectedIds(new Set());
  };

  const selectAll = () => {
    setSelectedIds(new Set(allIds));
  };

  const onSelectHeader = state => {
    state ? selectNone(): selectAll();
  };

  return /** @type {Types.RowSelectionOptions} */ ({
    ...rowSelectionOptions,
    onSelectHeader,
    onSelectNone: selectNone,
  });
}

/**
 * @param {Set<string>} set
 * @param {!Array<string>} arr
 * @returns {Set<string>}
 */
const addArrayToSet = (set, arr) => {
  const newSet = new Set(set);
  for (const el of arr) {
    newSet.add(el);
  }
  return newSet;
};

/**
 * @param {Set<string>} set
 * @param {!Array<string>} arr
 * @returns {Set<string>}
 */
const deleteArrayFromSet = (set, arr) => {
  const newSet = new Set(set);
  for (const el of arr) {
    newSet.delete(el);
  }
  return newSet;
};

/**
 * @typedef {{
 *   page: string,
 *   all: string,
 *   none: string,
 * }}
 */
let ActionNames;

/**
 * Creates utility functions for handling paginated row selection when all
 * IDs are known beforehand. This allows selections to be preserved across page
 * changes.
 *
 * @param {!Array<Types.Row>} pageData
 * @param {!Array<string>} allIds
 * @param {ActionNames | undefined} actionNames
 * @param {function(Set<string>): void} onSelectionChange
 * @param {boolean=} isSingleSelect
 * @return {Types.RowSelectionOptions}
 */
export function useSelectionForPaginatedData(pageData, allIds, actionNames, onSelectionChange, isSingleSelect=false) {
  const pageIds = pageData.map(({id}) => id);

  /**
   * @typedef {{
   *   numSelected: number,
   *   selectedIds: Set<string>,
   *   selectAllState: Types.CheckboxState,
   * }}
   */
  let State;

  /** @enum {string} */
  const ActionType = {
    SET_SELECTED: 'setSelected',
    ADD_MULTIPLE: 'addMultiple',
    DELETE_MULTIPLE: 'deleteMultiple',
    ADD_SINGLE: 'addSingle',
    DELETE_SINGLE: 'deleteSingle',
  };

  /**
   * @param {State} state
   * @param {{ type: ActionType, payload }} action
   * @returns {State}
   */
  const reducer = (state, action) => {
    let newSelected = state.selectedIds;

    switch (action.type) {
      case ActionType.SET_SELECTED:
        newSelected = action.payload;
        break;
      case ActionType.ADD_MULTIPLE:
        newSelected = addArrayToSet(state.selectedIds, action.payload);
        break;
      case ActionType.DELETE_MULTIPLE:
        newSelected = deleteArrayFromSet(state.selectedIds, action.payload);
        break;
      case ActionType.ADD_SINGLE:
        newSelected = new Set(isSingleSelect ? null : state.selectedIds);
        newSelected.add(action.payload);
        break;
      case ActionType.DELETE_SINGLE:
        newSelected = new Set(isSingleSelect ? null : state.selectedIds);
        newSelected.delete(action.payload); // no-op if isSingleSelect
        break;
    }

    onSelectionChange(newSelected);
    const numSelected = newSelected.size;

    let selectAllState;
    if (numSelected === 0) {
      selectAllState = Types.CheckboxState.UNSELECTED;
    } else if (numSelected === allIds.length) {
      selectAllState = Types.CheckboxState.SELECTED;
    } else {
      selectAllState = Types.CheckboxState.INDETERMINATE;
    }

    return {
      selectedIds: newSelected,
      numSelected,
      selectAllState,
    };
  };

  const [state, dispatch] = React.useReducer(reducer, {
    selectedIds: new Set(),
    numSelected: 0,
    selectAllState: Types.CheckboxState.UNSELECTED,
  });

  const isSelected = id => {
    return state.selectedIds.has(id);
  };

  const selectOne = (id, state) => {
    if (state) {
      dispatch({type: ActionType.DELETE_SINGLE, payload: id});
    } else {
      dispatch({type: ActionType.ADD_SINGLE, payload: id});
    }
  };


  const rowSelectionOptions = {
    isSelected,
    onSelectRow: selectOne,
    numSelected: state.numSelected,
    selectedIds: state.selectedIds,
    isSingleSelect: !!isSingleSelect,
  };

  if (isSingleSelect) {
    return rowSelectionOptions;
  }

  const selectNone = () => {
    dispatch({type: ActionType.SET_SELECTED, payload: new Set()});
  };

  const selectAll = () => {
    dispatch({type: ActionType.SET_SELECTED, payload: new Set(allIds)});
  };

  const selectAllOnPage = () => {
    dispatch({type: ActionType.ADD_MULTIPLE, payload: pageIds});
  };

  const deleteAllOnPage = () => {
    dispatch({type: ActionType.DELETE_MULTIPLE, payload: pageIds});
  };

  const selectHeader = state => {
    if (state === Types.CheckboxState.UNSELECTED) {
      selectAllOnPage();
    } else {
      deleteAllOnPage();
    }
  };

  return /** @type {Types.RowSelectionOptions} */ ({
    ...rowSelectionOptions,
    onSelectHeader: selectHeader,
    selectAllState: state.selectAllState,
    selectActions: [{
      name: actionNames.page,
      callback: selectAllOnPage,
    }, {
      name: actionNames.all,
      callback: selectAll,
    }, {
      name: actionNames.none,
      callback: selectNone,
    }],
    onSelectNone: selectNone,
  });
}

/**
 * Creates utility functions for handling paginated row selection when not all
 * IDs are known beforehand. This pattern supports selection within a page or
 * selecting all entries.
 *
 * @param {!Array<Types.Row>} pageData
 * @param {number | null} numEntries If null, represents an unknown number of entries.
 * @param {ActionNames} actionNames
 * @param {function({ type: string, selected: (Set<string>|undefined) }): void} onSelectionChange
 * @param {boolean=} isSingleSelect
 * @return {{
 *   onPageDataChange: function(): void,
 *   rowSelectionOptions: Types.RowSelectionOptions,
 * }}
 */
export function useSelectionForUnloadedData(
  pageData,
  numEntries,
  actionNames,
  onSelectionChange,
  isSingleSelect=false,
) {
  const pageIds = pageData.map(({id}) => id);

  /**
   * @typedef {{
   *   numSelected: (number | null),
   *   selectedIds: Set<string>,
   *   selectAllState: Types.CheckboxState,
   *   allSelected: boolean,
   * }}
   */
  let State;

  /** @enum {string} */
  const ActionType = {
    SET_SELECTED: 'setSelected',
    ADD_MULTIPLE: 'addMultiple',
    DELETE_MULTIPLE: 'deleteMultiple',
    ADD_SINGLE: 'addSingle',
    DELETE_SINGLE: 'deleteSingle',
    SELECT_ALL: 'selectAll',
    ON_PAGE_DATA_CHANGE: 'onPageDataChange',
  };

  /**
   * @param {State} state
   * @param {{ type: ActionType, payload }} action
   * @returns {State}
   */
  const reducer = (state, action) => {
    let newSelected = state.selectedIds;
    let newAllSelected = state.allSelected;
    let newSelectedValues;

    switch (action.type) {
      case ActionType.SET_SELECTED:
        newSelected = action.payload;
        newAllSelected = false;
        break;
      case ActionType.ADD_MULTIPLE:
        newSelected = addArrayToSet(state.selectedIds, action.payload);
        break;
      case ActionType.DELETE_MULTIPLE:
        newSelected = deleteArrayFromSet(state.selectedIds, action.payload);
        newAllSelected = false;
        break;
      case ActionType.ADD_SINGLE:
        newSelected = new Set(isSingleSelect ? null : state.selectedIds);
        newSelected.add(action.payload);
        break;
      case ActionType.DELETE_SINGLE:
        if (isSingleSelect) {
          newSelectedValues = null;
        } else if (newAllSelected) {
          newSelectedValues = pageIds;
        } else {
          newSelectedValues = state.selectedIds;
        }
        newSelected = new Set(newSelectedValues);
        newSelected.delete(action.payload); // no-op if isSingleSelect
        newAllSelected = false;
        break;
      case ActionType.SELECT_ALL:
        newSelected = action.payload;
        newAllSelected = true;
        break;
      case ActionType.ON_PAGE_DATA_CHANGE:
        newSelected = new Set();
        break;
    }

    if (newAllSelected) {
      onSelectionChange({type: 'all'});
    } else {
      onSelectionChange({type: 'page', selected: newSelected});
    }

    const numSelected = newAllSelected ? numEntries : newSelected.size;

    let selectAllState;
    if (newAllSelected) {
      selectAllState = Types.CheckboxState.SELECTED;
    } else if (numSelected === 0) {
      selectAllState = Types.CheckboxState.UNSELECTED;
    } else {
      selectAllState = Types.CheckboxState.INDETERMINATE;
    }

    return {
      selectedIds: newSelected,
      numSelected,
      selectAllState,
      allSelected: newAllSelected,
    };
  };

  const [state, dispatch] = React.useReducer(reducer, {
    selectedIds: new Set(),
    allSelected: false,
    numSelected: 0,
    selectAllState: Types.CheckboxState.UNSELECTED,
  });

  const isSelected = id => {
    if (state.allSelected) return true;
    return state.selectedIds.has(id);
  };

  const selectOne = (id, state) => {
    if (state) {
      dispatch({type: ActionType.DELETE_SINGLE, payload: id});
    } else {
      dispatch({type: ActionType.ADD_SINGLE, payload: id});
    }
  };

  let rowSelectionOptions = {
    isSelected,
    onSelectRow: selectOne,
    numSelected: state.numSelected,
    selectedIds: state.selectedIds,
    isSingleSelect: !!isSingleSelect,
  };


  const onPageDataChange = () => {
    dispatch({type: ActionType.ON_PAGE_DATA_CHANGE, payload: undefined});
  };

  if (!isSingleSelect) {
    const selectNone = () => {
      dispatch({type: ActionType.SET_SELECTED, payload: new Set()});
    };

    const selectAll = () => {
      dispatch({type: ActionType.SELECT_ALL, payload: new Set(pageIds)});
    };


    const selectAllOnPage = () => {
      dispatch({type: ActionType.ADD_MULTIPLE, payload: pageIds});
    };

    const deleteAllOnPage = () => {
      dispatch({type: ActionType.DELETE_MULTIPLE, payload: pageIds});
    };

    const selectHeader = state => {
      if (state === Types.CheckboxState.UNSELECTED) {
        selectAllOnPage();
      } else {
        deleteAllOnPage();
      }
    };

    rowSelectionOptions = /** @type {Types.RowSelectionOptions} */ ({
      ...rowSelectionOptions,
      selectAllState: state.selectAllState,
      onSelectNone: selectNone,
      onSelectHeader: selectHeader,
      selectActions: [{
        name: actionNames.page,
        callback: selectAllOnPage,
      }, {
        name: actionNames.all,
        callback: selectAll,
      }, {
        name: actionNames.none,
        callback: selectNone,
      }],
    });
  }

  return {
    onPageDataChange,
    rowSelectionOptions,
  };
}
